import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Gallery,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  Spinner,
  Title,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  BanIcon,
  CheckIcon,
  ExclamationCircleIcon,
  MicrochipIcon,
  SearchIcon,
  SyncAltIcon,
  TrashIcon,
  UndoIcon,
} from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useAdminGpuServer } from '../../../hooks/useAdminVerdaServers';
import type { VerdaFinding, VerdaGpuServer, VerdaGpuServerUsageRow } from '../../../types/models/Verda';
import { formatDateTime, formatMonthYear } from '../../../utils/dateFormat';
import { formatCurrencyAmount, formatPrice, formatUnitPrice } from '../../../utils/money';
import {
  type ActionAvailability,
  destroyAvailability,
  isNonPositiveMargin,
  markNotCreatedAvailability,
  refundAvailability,
  resolveAvailability,
  severityColor,
  verdaGpuStatusLabel,
} from './verdaGpuServers';
import VerdaDestroyServerModal from './VerdaDestroyServerModal';
import VerdaRefundServerModal from './VerdaRefundServerModal';
import VerdaMarkNotCreatedModal from './VerdaMarkNotCreatedModal';
import VerdaResolveServerModal from './VerdaResolveServerModal';

type ModalKind = 'destroy' | 'refund' | 'mark-not-created' | 'resolve';

/** Re-evaluate time-based enable rules (the 30-minute rule) without a reload. */
const NOW_TICK_MS = 30_000;

/** A strictly positive integer route param, else `null` (rendered as not-found without a call). */
const parseServerId = (value: string | undefined): number | null =>
  value !== undefined && /^[1-9]\d{0,9}$/.test(value) ? Number(value) : null;

interface ActionButtonProps {
  label: string;
  availability: ActionAvailability;
  variant: 'danger' | 'secondary';
  icon: React.ReactNode;
  onClick: () => void;
}

/** A title-bar action; when not allowed it stays focusable (aria-disabled) and its tooltip says why. */
const ActionButton: React.FunctionComponent<ActionButtonProps> = ({ label, availability, variant, icon, onClick }) => {
  const button = (
    <Button
      variant={variant}
      icon={icon}
      isAriaDisabled={!availability.enabled}
      onClick={availability.enabled ? onClick : undefined}
    >
      {label}
    </Button>
  );
  return availability.enabled ? button : <Tooltip content={availability.reason}>{button}</Tooltip>;
};

const Mono: React.FunctionComponent<{ value: string | null }> = ({ value }) =>
  value === null ? <>—</> : <span className="pf-v6-u-font-family-monospace">{value}</span>;

const Field: React.FunctionComponent<{ term: string; children: React.ReactNode }> = ({ term, children }) => (
  <DescriptionListGroup>
    <DescriptionListTerm>{term}</DescriptionListTerm>
    <DescriptionListDescription>{children}</DescriptionListDescription>
  </DescriptionListGroup>
);

const eur6 = (value: string) => formatCurrencyAmount(value, 'EUR');

/**
 * Admin › Verda › GPU server (`/admin/verda/servers/:serverId`) — one resold GPU server, staff view
 * (`GET /admin/verda/servers/{id}`, a database read): the provider block, price and margin, usage,
 * timeline, its attention items, hour rows and notices, and the four staff actions — Destroy, Refund,
 * Mark not created, Resolve — each enabled by the documented rules (the backend re-checks every one
 * and its refusal is shown verbatim). Not gated on impersonation: these routes read no client.
 * After every action the detail is refetched (an action's returned server has no usage summary).
 */
function VerdaServerDetail() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  const { serverId } = useParams<{ serverId: string }>();
  const id = parseServerId(serverId);
  const { server, usage, notices, loading, error, notFound, refetch } = useAdminGpuServer(isStaff ? id : null);
  const [modal, setModal] = useState<ModalKind | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [usagePage, setUsagePage] = useState(1);
  const [usagePerPage, setUsagePerPage] = useState(25);

  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), NOW_TICK_MS);
    return () => window.clearInterval(handle);
  }, []);

  const usageRows = useMemo(() => usage ?? [], [usage]);
  const usagePageRows = useMemo(
    () => usageRows.slice((usagePage - 1) * usagePerPage, usagePage * usagePerPage),
    [usageRows, usagePage, usagePerPage],
  );

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }
  if (!isStaff) {
    return <NotFound />;
  }

  const onSetUsagePage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, page: number) =>
    setUsagePage(page);
  const onUsagePerPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, perPage: number) => {
    setUsagePerPage(perPage);
    setUsagePage(1);
  };

  const refresh = () => {
    void refetch();
  };

  const renderActions = (loaded: VerdaGpuServer) => (
    <Flex spaceItems={{ default: 'spaceItemsSm' }}>
      <FlexItem>
        <ActionButton
          label="Destroy"
          variant="danger"
          icon={<TrashIcon />}
          availability={destroyAvailability(loaded)}
          onClick={() => setModal('destroy')}
        />
      </FlexItem>
      <FlexItem>
        <ActionButton
          label="Refund"
          variant="secondary"
          icon={<UndoIcon />}
          availability={refundAvailability(loaded)}
          onClick={() => setModal('refund')}
        />
      </FlexItem>
      <FlexItem>
        <ActionButton
          label="Mark not created"
          variant="secondary"
          icon={<BanIcon />}
          availability={markNotCreatedAvailability(loaded, now)}
          onClick={() => setModal('mark-not-created')}
        />
      </FlexItem>
      <FlexItem>
        <ActionButton
          label="Resolve"
          variant="secondary"
          icon={<CheckIcon />}
          availability={resolveAvailability(loaded)}
          onClick={() => setModal('resolve')}
        />
      </FlexItem>
      <FlexItem>
        <Button
          variant="secondary"
          icon={<SyncAltIcon />}
          isLoading={loading}
          spinnerAriaValueText="Refreshing the GPU server"
          onClick={() => {
            if (!loading) {
              refresh();
            }
          }}
        >
          Refresh
        </Button>
      </FlexItem>
    </Flex>
  );

  const renderUsageTable = (loaded: VerdaGpuServer) => (
    <>
      <Title headingLevel="h2" size="lg" className="pf-v6-u-mt-lg pf-v6-u-mb-sm">
        Hour rows
      </Title>
      {loaded.usage.hours > usageRows.length && (
        <Content component="p" className="pf-v6-u-text-color-subtle">
          The newest {usageRows.length} of {loaded.usage.hours} hours are listed.
        </Content>
      )}
      {usageRows.length === 0 ? (
        <Content component="p">No hour was billed.</Content>
      ) : (
        <>
          <Pagination
            itemCount={usageRows.length}
            perPage={usagePerPage}
            page={usagePage}
            onSetPage={onSetUsagePage}
            onPerPageSelect={onUsagePerPage}
            isCompact
          />
          <Table aria-label="GPU server hour rows" variant="compact">
            <Thead>
              <Tr>
                <Th>Hour start</Th>
                <Th>Period</Th>
                <Th>Index</Th>
                <Th>Debit TTC</Th>
                <Th>Cost</Th>
                <Th>Wallet tx</Th>
                <Th>Refunded</Th>
                <Th>Invoice</Th>
              </Tr>
            </Thead>
            <Tbody>
              {usagePageRows.map((row: VerdaGpuServerUsageRow) => (
                <Tr key={row.id}>
                  <Td dataLabel="Hour start" modifier="nowrap">
                    {formatDateTime(row.hour_start)}
                  </Td>
                  <Td dataLabel="Period">{formatMonthYear(row.period)}</Td>
                  <Td dataLabel="Index">{row.hour_index}</Td>
                  <Td dataLabel="Debit TTC" modifier="nowrap">
                    {formatPrice(row.debit_ttc)}
                  </Td>
                  <Td dataLabel="Cost" modifier="nowrap">
                    {eur6(row.cost)}
                  </Td>
                  <Td dataLabel="Wallet tx">{row.wallet_transaction_id ?? '—'}</Td>
                  <Td dataLabel="Refunded" modifier="nowrap">
                    {row.refunded_at === null
                      ? '—'
                      : `${formatDateTime(row.refunded_at)}${row.refund_wallet_transaction_id !== null ? ` (tx ${row.refund_wallet_transaction_id})` : ''}`}
                  </Td>
                  <Td dataLabel="Invoice">
                    {row.invoice_id === null ? (
                      '—'
                    ) : (
                      <Link to={`/admin/verda?tab=invoices&invoice=${row.invoice_id}`}>#{row.invoice_id}</Link>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Pagination
            itemCount={usageRows.length}
            perPage={usagePerPage}
            page={usagePage}
            onSetPage={onSetUsagePage}
            onPerPageSelect={onUsagePerPage}
            variant="bottom"
          />
        </>
      )}
    </>
  );

  const renderNotices = (list: VerdaFinding[]) => (
    <>
      <Title headingLevel="h2" size="lg" className="pf-v6-u-mt-lg pf-v6-u-mb-sm">
        Notices
      </Title>
      {list.length === 0 ? (
        <Content component="p">No notice about this server.</Content>
      ) : (
        <Table aria-label="GPU server notices" variant="compact">
          <Thead>
            <Tr>
              <Th>Detected</Th>
              <Th>Severity</Th>
              <Th>Stage</Th>
              <Th>Message</Th>
              <Th>Emailed</Th>
            </Tr>
          </Thead>
          <Tbody>
            {list.map((notice) => (
              <Tr key={notice.id}>
                <Td dataLabel="Detected" modifier="nowrap">
                  {formatDateTime(notice.detected_at)}
                </Td>
                <Td dataLabel="Severity">
                  {notice.severity === null ? (
                    '—'
                  ) : (
                    <Label color={severityColor(notice.severity)} isCompact>
                      {notice.severity}
                    </Label>
                  )}
                </Td>
                <Td dataLabel="Stage">
                  <Mono value={notice.stage} />
                </Td>
                <Td dataLabel="Message">{notice.message ?? '—'}</Td>
                <Td dataLabel="Emailed" modifier="nowrap">
                  {formatDateTime(notice.sent_at)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </>
  );

  const renderDetail = (loaded: VerdaGpuServer) => {
    const status = verdaGpuStatusLabel(loaded.status);
    return (
      <>
        {error !== null && (
          <Alert variant="danger" isInline title="Could not refresh the GPU server" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {loaded.attention.map((item) => (
          <Alert
            key={item.code}
            variant={item.severity === 'CRITICAL' ? 'danger' : 'warning'}
            isInline
            title={item.code}
            className="pf-v6-u-mb-sm"
          >
            {item.message}
          </Alert>
        ))}
        <Gallery hasGutter minWidths={{ default: '100%', lg: '420px' }} className="pf-v6-u-mt-md">
          <Card isCompact>
            <CardTitle>Server</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact>
                <Field term="Client">
                  {loaded.client.name ?? '—'} (#{loaded.client.id})
                </Field>
                <Field term="Product">
                  {loaded.product === null ? (
                    '—'
                  ) : (
                    <>
                      {loaded.product.label} <Mono value={loaded.product.product_key} />
                    </>
                  )}
                </Field>
                <Field term="Instance type">
                  <Mono value={loaded.instance_type} />
                </Field>
                <Field term="Status">
                  <Label color={status.color} isCompact>
                    {status.label}
                  </Label>{' '}
                  <span className="pf-v6-u-text-color-subtle">(tenant: {loaded.tenant_status})</span>
                </Field>
                <Field term="Location">{loaded.location_code}</Field>
                <Field term="Image">
                  <Mono value={loaded.image} />
                </Field>
                <Field term="System disk">{loaded.disk_gb} GB</Field>
                <Field term="IP">{loaded.ip ?? '—'}</Field>
              </DescriptionList>
            </CardBody>
          </Card>
          <Card isCompact>
            <CardTitle>Verda</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact>
                <Field term="Instance id">
                  <Mono value={loaded.verda.instance_id} />
                </Field>
                <Field term="OS volume id">
                  <Mono value={loaded.verda.os_volume_id} />
                </Field>
                <Field term="Verda status">{loaded.verda.status ?? '—'}</Field>
                <Field term="Absent count">{loaded.verda.absent_count}</Field>
                <Field term="Last seen">{formatDateTime(loaded.verda.last_seen_at)}</Field>
                <Field term="Installation tag">
                  <Mono value={loaded.namespace} />
                </Field>
                <Field term="Correlation token">
                  <Mono value={loaded.correlation_token} />
                </Field>
              </DescriptionList>
            </CardBody>
          </Card>
          <Card isCompact>
            <CardTitle>Price and margin</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact>
                <Field term="Sale / h HT">{formatUnitPrice(loaded.price.sale_ht_per_hour)}</Field>
                <Field term="Instance / h HT">{formatUnitPrice(loaded.price.sale_instance_ht)}</Field>
                <Field term="Disk / h HT">{formatUnitPrice(loaded.price.sale_disk_ht)}</Field>
                <Field term="VAT">{loaded.price.vat_rate} %</Field>
                <Field term="Cost instance / h">{eur6(loaded.price.cost_instance_per_hour)}</Field>
                <Field term="Cost disk / h">{eur6(loaded.price.cost_disk_per_hour)}</Field>
                <Field term="Cost source">{loaded.price.cost_source}</Field>
                <Field term="Margin / h">
                  <span
                    className={
                      isNonPositiveMargin(loaded.price.margin_ht_per_hour)
                        ? 'pf-v6-u-text-color-status-danger'
                        : undefined
                    }
                  >
                    {eur6(loaded.price.margin_ht_per_hour)}
                  </span>
                </Field>
              </DescriptionList>
            </CardBody>
          </Card>
          <Card isCompact>
            <CardTitle>Usage</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact>
                <Field term="Hours billed">{loaded.usage.hours}</Field>
                <Field term="Debited TTC">{formatPrice(loaded.usage.debited_ttc)}</Field>
                <Field term="Refunded TTC">{formatPrice(loaded.usage.refunded_ttc)}</Field>
                <Field term="Unrefunded hours">{loaded.usage.unrefunded_hours}</Field>
              </DescriptionList>
            </CardBody>
          </Card>
          <Card isCompact>
            <CardTitle>Timeline</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact>
                <Field term="Started">{formatDateTime(loaded.started_at)}</Field>
                <Field term="Deploy attempted">{formatDateTime(loaded.deploy_attempted_at)}</Field>
                <Field term="Running">{formatDateTime(loaded.running_at)}</Field>
                <Field term="Paid through">{formatDateTime(loaded.paid_through)}</Field>
                <Field term="Destroy requested">{formatDateTime(loaded.destroy_requested_at)}</Field>
                <Field term="Destroy reason">
                  <Mono value={loaded.destroy_reason} />
                </Field>
                <Field term="Destroyed">{formatDateTime(loaded.destroyed_at)}</Field>
                <Field term="Failed">{formatDateTime(loaded.failed_at)}</Field>
                <Field term="Failure reason">
                  <Mono value={loaded.failure_reason} />
                </Field>
              </DescriptionList>
            </CardBody>
          </Card>
          <Card isCompact>
            <CardTitle>Resolution</CardTitle>
            <CardBody>
              {loaded.resolution === null ? (
                <Content component="p">Not resolved.</Content>
              ) : (
                <DescriptionList isHorizontal isCompact>
                  <Field term="Resolved">{formatDateTime(loaded.resolution.resolved_at)}</Field>
                  <Field term="By">
                    {loaded.resolution.resolved_by === null ? '—' : `user #${loaded.resolution.resolved_by}`}
                  </Field>
                  <Field term="Note">{loaded.resolution.note}</Field>
                </DescriptionList>
              )}
            </CardBody>
          </Card>
        </Gallery>
        {renderUsageTable(loaded)}
        {renderNotices(notices ?? [])}
      </>
    );
  };

  const renderContent = () => {
    if (notFound) {
      return (
        <EmptyState titleText="GPU server not found" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>No GPU server has this id.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/admin/verda?tab=servers" />}>
                Back to the GPU servers
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    if (server !== null) {
      return renderDetail(server);
    }
    if (error !== null) {
      return (
        <EmptyState
          titleText="Unable to load the GPU server"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={refresh}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    return (
      <Bullseye>
        <Spinner size="lg" aria-label="Loading the GPU server" />
      </Bullseye>
    );
  };

  const title = server?.hostname ?? (notFound ? 'GPU server not found' : 'GPU server');
  const status = server !== null ? verdaGpuStatusLabel(server.status) : null;

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="VerdaServerBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem to="/admin/verda?tab=servers">Verda</BreadcrumbItem>
          <BreadcrumbItem isActive>{server?.hostname ?? serverId}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <MicrochipIcon />
              </Icon>
              <span className={server !== null ? 'pf-v6-u-font-family-monospace' : undefined}>{title}</span>{' '}
              {status !== null && <Label color={status.color}>{status.label}</Label>}
            </Content>
            <Content component="p">
              {server !== null
                ? `GPU server #${server.id} of ${server.client.name ?? `client #${server.client.id}`}, staff view.`
                : 'A resold GPU server, staff view.'}
            </Content>
          </FlexItem>
          {server !== null && !notFound && <FlexItem>{renderActions(server)}</FlexItem>}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      {server !== null && modal === 'destroy' && (
        <VerdaDestroyServerModal server={server} onClose={() => setModal(null)} onChanged={refresh} />
      )}
      {server !== null && modal === 'refund' && (
        <VerdaRefundServerModal server={server} usage={usageRows} onClose={() => setModal(null)} onChanged={refresh} />
      )}
      {server !== null && modal === 'mark-not-created' && (
        <VerdaMarkNotCreatedModal server={server} onClose={() => setModal(null)} onChanged={refresh} />
      )}
      {server !== null && modal === 'resolve' && (
        <VerdaResolveServerModal server={server} onClose={() => setModal(null)} onChanged={refresh} />
      )}
    </>
  );
}

export default VerdaServerDetail;
